import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  CardCoverageStatus,
  So5LeaderboardSeasonality,
} from '@sorare/core/src/__generated__/globalTypes';
import { isType } from '@sorare/core/src/lib/gql';

import type { useCompetitionsEligibility_anyCard } from './__generated__/useCompetitionsEligibility.graphql.ts';

type EligibleLeaderboard =
  useCompetitionsEligibility_anyCard['eligibleUpcomingLeagueTracks'][number]['entrySo5Leaderboard'];

const sortCompetitions = (a: EligibleLeaderboard, b: EligibleLeaderboard) => {
  if (a.seasonality && !b.seasonality) {
    return -1;
  }
  if (!a.seasonality && b.seasonality) {
    return -1;
  }
  if (a.displayName < b.displayName) {
    return -1;
  }
  if (a.displayName > b.displayName) {
    return 1;
  }
  return 0;
};

const reduceLeaderboards = (
  rarityTyped: string,
  seasonalities: (So5LeaderboardSeasonality | null)[]
) => {
  return (acc: EligibleLeaderboard[], l: EligibleLeaderboard) => {
    if (
      l.mainRarityType === rarityTyped &&
      seasonalities.includes(l.seasonality) &&
      !acc.some(accL => accL.so5League.displayName === l.so5League.displayName)
    ) {
      acc.push(l);
    }
    return acc;
  };
};

export const useCompetitionsEligibility = <
  T extends useCompetitionsEligibility_anyCard,
>(
  card: T
) => {
  if (
    isType(card, 'Card') &&
    card.coverageStatus === CardCoverageStatus.NOT_COVERED
  ) {
    return { inSeasonLeaderboards: [], classicCompetitions: [] };
  }
  const leaderboards = card.eligibleUpcomingLeagueTracks.map(
    lt => lt.entrySo5Leaderboard
  );
  const inSeasonLeaderboards = card.inSeasonEligible
    ? leaderboards
        .reduce<T['eligibleUpcomingLeagueTracks'][0]['entrySo5Leaderboard'][]>(
          reduceLeaderboards(card.rarityTyped, [
            So5LeaderboardSeasonality.IN_SEASON,
          ]),
          []
        )
        .sort(sortCompetitions)
    : [];
  const classicCompetitions = leaderboards
    .reduce<T['eligibleUpcomingLeagueTracks'][0]['entrySo5Leaderboard'][]>(
      reduceLeaderboards(card.rarityTyped, [
        So5LeaderboardSeasonality.ALL_SEASONS,
        null,
      ]),
      []
    )
    .sort(sortCompetitions);
  return { inSeasonLeaderboards, classicCompetitions };
};

useCompetitionsEligibility.fragments = {
  anyCard: gql`
    fragment useCompetitionsEligibility_anyCard on AnyCardInterface {
      slug
      rarityTyped
      inSeasonEligible
      eligibleUpcomingLeagueTracks {
        slug
        displayName
        entrySo5Leaderboard {
          slug
          displayName
          mainRarityType
          seasonality
          so5League {
            slug
            displayName
          }
        }
      }
      ... on Card {
        slug
        coverageStatus
      }
    }
  ` as TypedDocumentNode<useCompetitionsEligibility_anyCard>,
};
